// job-web-main/frontend/src/components/dashboard/DocumentsTab.js
import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Clock, AlertTriangle, Upload, Camera, Eye, Plus } from 'lucide-react';
import { getDashboardDocuments, replaceDashboardDocument, addDashboardDocument, fetchDashboardDocumentPreviewUrl, getMe, getDashboardStatus, portalErrorMessage } from '../../api/portal';
import { client } from '../../api/portal';
import './dashboardTabs.css';

const STATUS_ICON = { approved: CheckCircle2, review: Clock, rejected: AlertTriangle };
const STATUS_LABEL = { approved: 'Approved', review: 'In review', rejected: 'Needs attention' };
// Generic, always-applicable tips (artifact-fidelity, 2026-07-25) — the backend only stores a
// free-text `reason` from the reviewer, no structured rejection-type field to pick more specific tips
// from, so these are broad photo-quality guidance rather than tailored per-reason advice.
const REJECTION_TIPS = ['Make sure all 4 corners of the document are visible', 'Avoid glare — try a shaded, well-lit spot'];
// Real per-slot caps (must match ApplyPage.js's SLOTS / backend SLOT_MAX_COUNT).
const CAPS_NOTE = 'You can add up to 3 CVs, 5 education certificates and 5 experience certificates. Approved documents can’t be removed — only replaced if we ask you to.';

// The "add a document" list is data-driven (owner ask 2026-07-27): fetched from
// GET /public/dashboard/addable-documents = the hardcoded CV+Passport defaults ∪ the admin's
// mandatoryForAll DocumentTypes, each { slot, name, mandatory }. `slot` is the upload key (built-in or
// `dt:<id>`); `name` labels it and detects which the candidate already holds (name is the canonical
// cross-reference). The full optional catalogue is intentionally NOT offered here.
const normName = (s) => String(s || '').trim().toLowerCase();

export default function DocumentsTab() {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  // Documents lock once staff promote (verify) the account — only replacing a doc we asked them to redo
  // stays possible then. Hide the "add a document" affordance in that state (the server 403s it anyway).
  const [promoted, setPromoted] = useState(false);
  const [addableTypes, setAddableTypes] = useState([]);
  // True when the whole application was rejected (staff rejected at cv-verification, e.g. "resubmit
  // passport") — surfaces the replace CTA on every doc row, since that rejection often leaves no single
  // doc marked 'rejected'. Distinct from the per-doc `d.status === 'rejected'` case.
  const [appRejected, setAppRejected] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  // { id } of the rejected document being replaced, OR { addType } of a fresh document being added.
  const pendingTargetRef = useRef(null);

  function load() {
    getDashboardDocuments().then(setDocs).catch((e) => setError(portalErrorMessage(e, 'Could not load your documents.')));
    // Re-fetched on every reload so the app-level rejection flag stays in sync after a replace (the
    // backend flips replaced docs back into review). Non-fatal — a status failure just hides the extra CTA.
    // NOTE: do NOT include `s.docIssue` here (owner ask 2026-07-27) — docIssue is set when a SPECIFIC
    // document is rejected, and that case is already handled per-row via `d.status === 'rejected'`.
    // Folding it into appRejected wrongly blanketed the "another look" CTA onto EVERY document. Only a
    // genuine whole-application rejection (no single doc flagged) should surface the CTA on all rows.
    getDashboardStatus().then((s) => setAppRejected(s.stateId === 'application_rejected')).catch(() => {});
  }
  useEffect(load, []);
  useEffect(() => { getMe().then((a) => setPromoted(!!a.promoted)).catch(() => {}); }, []);
  // The addable set (defaults + mandatoryForAll) comes from the server so it tracks admin config.
  useEffect(() => { client.get('/public/dashboard/addable-documents').then((r) => setAddableTypes(r.data?.documents || [])).catch(() => {}); }, []);

  async function handleView(id) {
    setViewingId(id);
    try {
      const url = await fetchDashboardDocumentPreviewUrl(id);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not open that document. Please try again.'));
    } finally {
      // Only clear if THIS request's id is still the current one — clicking a second row's View before
      // the first resolves must not let the first request's finally wipe the second's in-flight state.
      setViewingId((current) => (current === id ? null : current));
    }
  }

  function triggerReplace(docId, useCamera) {
    pendingTargetRef.current = { id: docId };
    (useCamera ? cameraInputRef : fileInputRef).current?.click();
  }
  function triggerAdd(addType) {
    pendingTargetRef.current = { addType };
    fileInputRef.current?.click();
  }

  async function handleFileChosen(e) {
    const file = e.target.files?.[0];
    const target = pendingTargetRef.current;
    e.target.value = '';
    if (!file || !target) return;

    const busyKey = target.id || `add:${target.addType}`;
    setUploadingFor(busyKey);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('type', target.addType || 'other');
      const { data: staged } = await client.post('/public/documents/stage', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (target.id) {
        await replaceDashboardDocument(target.id, staged.stageId);
      } else {
        await addDashboardDocument(target.addType, staged.stageId);
      }
      load();
    } catch (err) {
      // 403 once promoted — surface the server's "documents are locked, ask us to replace" message.
      setError(portalErrorMessage(err, 'Could not upload that file — please try again.'));
    } finally {
      setUploadingFor(null);
    }
  }

  if (error && !docs) return <div className="apply-error" role="alert">{error}</div>;
  if (!docs) return null;

  const heldNames = new Set(docs.map((d) => normName(d.name)));
  const addable = addableTypes.filter((a) => !heldNames.has(normName(a.name)));

  return (
    <div className="dash-panel">
      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={handleFileChosen} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileChosen} />

      {/* Surface a transient upload error above the list (the early-return only handles the load error). */}
      {error && <div className="apply-error" role="alert" style={{ marginBottom: 10 }}>{error}</div>}

      {docs.map((d) => {
        const Icon = STATUS_ICON[d.status] || Clock;
        return (
          <div key={d.id} className="dtab-doc-row">
            <div className="dtab-doc-toprow">
              <span className="dtab-doc-name">{d.name}</span>
              <span className={`dtab-doc-status ${d.status}`}><Icon size={12} />{STATUS_LABEL[d.status]}</span>
            </div>
            <div className="dtab-doc-metarow">
              <p className="dtab-doc-meta">Uploaded {new Date(d.uploadedAt).toLocaleDateString()}</p>
              <button type="button" className="dtab-doc-view" disabled={viewingId === d.id} onClick={() => handleView(d.id)}>
                <Eye size={13} /> {viewingId === d.id ? 'Opening…' : 'View'}
              </button>
            </div>
            {(d.status === 'rejected' || appRejected) && (
              <>
                {d.status === 'rejected' ? (
                  <>
                    <p className="dtab-doc-reason">{d.reason}</p>
                    <ul className="dtab-doc-tips">
                      {REJECTION_TIPS.map((tip) => <li key={tip}>{tip}</li>)}
                    </ul>
                  </>
                ) : (
                  <p className="dtab-doc-reason">We asked for another look — upload a new copy.</p>
                )}
                <div className="dtab-doc-fixctas">
                  <button type="button" className="dtab-add-doc" disabled={uploadingFor === d.id} onClick={() => triggerReplace(d.id, true)}>
                    <Camera size={14} /> {uploadingFor === d.id ? 'Uploading…' : 'Take a new photo'}
                  </button>
                  <button type="button" className="dtab-add-doc" disabled={uploadingFor === d.id} onClick={() => triggerReplace(d.id, false)}>
                    <Upload size={14} /> {uploadingFor === d.id ? 'Uploading…' : 'Upload a file'}
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* Add a document the candidate forgot during their application — only while the file is still
          unlocked (before staff verification). Once promoted the server 403s these, so the section is
          hidden and only rejected-doc replacement (above) remains. */}
      {!promoted && addable.length > 0 && (
        <div className="dtab-add-section">
          <p className="dtab-add-title">Add a document</p>
          <p className="dtab-add-sub">Forgot something? Add it here before our team reviews your file.</p>
          {addable.map((a) => (
            <button key={a.slot} type="button" className="dtab-add-doc" disabled={uploadingFor === `add:${a.slot}`} onClick={() => triggerAdd(a.slot)}>
              <Plus size={14} /> {uploadingFor === `add:${a.slot}` ? 'Uploading…' : `Add ${a.name}`}
            </button>
          ))}
        </div>
      )}

      <p className="dtab-caps-note">{CAPS_NOTE}</p>
    </div>
  );
}
